/**
 * useAiSubtitles
 *
 * Drives an AI subtitle-generation job and exposes the same small state machine
 * shape as useAiTrailer / useAiVideoAnalysis, so the UI can render a familiar
 * progress panel. Owns no form knowledge — it runs the service, tracks
 * stage/progress, and hands back the finished subtitle list. The Media Assets
 * subtitle section decides what to do with it (prefill the subtitle rows).
 *
 * Status lifecycle:
 *   idle → running → complete
 *                 ↳ failed  (retry via generate())
 *                 ↳ idle    (reset())
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateSubtitles, SUBTITLE_STAGES } from "../services/ai";

export const SUBTITLE_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
};

export default function useAiSubtitles() {
  const [status, setStatus] = useState(SUBTITLE_STATUS.IDLE);
  const [activeStage, setActiveStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus(SUBTITLE_STATUS.IDLE);
    setActiveStage(null);
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  /**
   * Start (or restart) subtitle generation for a video.
   * @param {object} video
   * @param {object} [opts] { targetLanguages, diarize }
   *        `diarize` asks for speaker labels so a dub of the same video can reuse this
   *        transcript instead of paying for a second one.
   */
  const generate = useCallback((video, opts = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(SUBTITLE_STATUS.RUNNING);
    setError(null);
    setResult(null);
    setProgress(0);
    setActiveStage(SUBTITLE_STAGES[0]?.id ?? null);

    generateSubtitles({
      video,
      targetLanguages: opts.targetLanguages,
      diarize: opts.diarize,
      transcribeLanguages: opts.transcribeLanguages,
      signal: controller.signal,
      onStage: (stageId, pct) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (stageId !== "complete") setActiveStage(stageId);
        if (typeof pct === "number") setProgress(pct);
      },
    })
      .then((res) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult(res);
        setProgress(100);
        setActiveStage("complete");
        setStatus(SUBTITLE_STATUS.COMPLETE);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        if (!mountedRef.current) return;
        setError(err?.message || "Subtitle generation failed. You can retry or add subtitles manually.");
        setStatus(SUBTITLE_STATUS.FAILED);
      });

    return controller;
  }, []);

  return {
    status,
    stages: SUBTITLE_STAGES,
    activeStage,
    progress,
    result,
    error,
    generate,
    reset,
    isIdle: status === SUBTITLE_STATUS.IDLE,
    isRunning: status === SUBTITLE_STATUS.RUNNING,
    isComplete: status === SUBTITLE_STATUS.COMPLETE,
    isFailed: status === SUBTITLE_STATUS.FAILED,
  };
}
